import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  loadSites,
  loadTemplates,
  loadSiteState,
  saveSiteState,
  uploadFile,
  getFileUrl,
  getFileObjectUrl,
  deleteFile,
  newId,
  captureGeo,
  notifySectionCompletedStub,
} from "./siteCheckStore";
import { Site, Template, Question, SiteState, PhotoEntry } from "./types";
// Reuse SMRT COP user-view styling (cards, status pills, action icons)
// and SMRT Docs modal chrome (overlay + dark modal box + header/body)
// so this view looks identical to the existing /smrt-cop crew flow.
import "../ManageSMRTCOP.css";
import "../ManageSMRTDocs.css";
// sitecheck.css carries the small additions SMRT COP doesn't have —
// voice-note mic buttons, per-photo geo badges, dark wizard cards.
import "./sitecheck.css";

type WizardState = { siteId: string; sectionIdx: number } | null;

// Status is derived from saved completion data — no separate status
// field on the site record. Mirrors how SMRT COP buckets sites for
// the crew landing page (Active / In-Progress / Complete colours).
type SiteStatus = "Not Started" | "In Progress" | "Complete";

function deriveStatus(template: Template | undefined, state: SiteState): SiteStatus {
  if (!template || template.sections.length === 0) return "Not Started";
  const total = template.sections.length;
  const done = template.sections.filter((s) => state.submitted[s.id]).length;
  if (done === 0) return "Not Started";
  if (done < total) return "In Progress";
  return "Complete";
}

function statusClass(s: SiteStatus): string {
  // Maps to the smrtcop-status colour modifiers from ManageSMRTCOP.css
  // so we get the same yellow / blue / orange palette as SMRT COP.
  if (s === "Not Started") return "assign-crew";
  if (s === "In Progress") return "in-progress";
  return "complete";
}

export default function SiteCheck() {
  const { user } = useAuth();
  const userId: string = user?.userID || "";

  const [sites, setSites] = useState<Site[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [wizard, setWizard] = useState<WizardState>(null);
  const [viewingSiteId, setViewingSiteId] = useState<string | null>(null);
  // Per-site state cache, hydrated async on mount and after each wizard
  // save. The landing list reads completion counts from here without
  // re-fetching on every row render.
  const [stateBySite, setStateBySite] = useState<Map<string, SiteState>>(
    () => new Map(),
  );
  // Bumped on each section save so the per-site state cache re-fetches.
  const [stateRev, setStateRev] = useState(0);

  // Initial load — sites + templates from backend.
  useEffect(() => {
    let cancelled = false;
    Promise.all([loadSites(), loadTemplates()])
      .then(([s, t]) => {
        if (cancelled) return;
        setSites(s);
        setTemplates(t);
      })
      .catch((err) => console.error("[SiteCheck] failed to load:", err));
    return () => {
      cancelled = true;
    };
  }, []);

  const mySites = useMemo(
    () => sites.filter((s) => s.assignedUserIds.includes(userId)),
    [sites, userId],
  );

  // Hydrate per-site state for all visible sites. Re-runs when the
  // visible-site set changes or stateRev bumps (after a wizard save).
  useEffect(() => {
    let cancelled = false;
    Promise.all(
      mySites.map((s) =>
        loadSiteState(s.id).catch(() => ({
          siteId: s.id,
          answers: {},
          submitted: {},
          updatedAt: new Date().toISOString(),
        })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const m = new Map<string, SiteState>();
      results.forEach((st, i) => m.set(mySites[i].id, st));
      setStateBySite(m);
    });
    return () => {
      cancelled = true;
    };
  }, [mySites, stateRev]);

  const viewingSite = viewingSiteId ? sites.find((s) => s.id === viewingSiteId) : null;
  const viewingTemplate = viewingSite
    ? templates.find((t) => t.id === viewingSite.templateId)
    : null;
  const viewingState = viewingSite ? stateBySite.get(viewingSite.id) : null;

  return (
    <div className="smrtcop-page">
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <h1 className="smrtcop-title">Site Check</h1>

        {mySites.length === 0 && (
          <div
            className="smrtcop-card"
            style={{ padding: 30, textAlign: "center", color: "#9aa0a6" }}
          >
            No sites assigned to you.
          </div>
        )}

        {mySites.length > 0 && (
          <div className="smrtcop-card">
            <div
              className="smrtcop-overview-header"
              style={{ gridTemplateColumns: "1.5fr 1.2fr 1fr 1fr" }}
            >
              <div>Site</div>
              <div>Address</div>
              <div>Status</div>
              <div style={{ textAlign: "center" }}>Actions</div>
            </div>

            {mySites.map((site) => {
              const tpl = templates.find((t) => t.id === site.templateId);
              const st = stateBySite.get(site.id);
              const status: SiteStatus = st ? deriveStatus(tpl, st) : "Not Started";
              return (
                <div
                  key={site.id}
                  className="smrtcop-overview-row"
                  style={{ gridTemplateColumns: "1.5fr 1.2fr 1fr 1fr" }}
                >
                  <div style={{ fontWeight: 600 }}>{site.name}</div>
                  <div style={{ color: "#9aa0a6", fontSize: "0.85rem" }}>
                    {site.address || "—"}
                  </div>
                  <div className={`smrtcop-status ${statusClass(status)}`}>
                    {status}
                  </div>
                  <div className="smrtcop-actions">
                    <button
                      className="smrtcop-btn-icon smrtcop-btn-view"
                      title="View site info"
                      onClick={() => setViewingSiteId(site.id)}
                    >
                      👁
                    </button>
                    <button
                      className="smrtcop-btn-icon smrtcop-btn-issue"
                      title={status === "Complete" ? "Re-open" : "Start"}
                      onClick={() => setWizard({ siteId: site.id, sectionIdx: 0 })}
                    >
                      ▶
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {viewingSite && viewingTemplate && (
        <SiteInfoModal
          site={viewingSite}
          template={viewingTemplate}
          state={
            viewingState ?? {
              siteId: viewingSite.id,
              answers: {},
              submitted: {},
              updatedAt: new Date().toISOString(),
            }
          }
          onClose={() => setViewingSiteId(null)}
        />
      )}

      {wizard && (
        <SectionWizard
          site={sites.find((s) => s.id === wizard.siteId)!}
          template={
            templates.find(
              (t) => t.id === sites.find((s) => s.id === wizard.siteId)?.templateId,
            )!
          }
          startIdx={wizard.sectionIdx}
          userId={userId}
          onClose={() => {
            setWizard(null);
            setStateRev((r) => r + 1);
          }}
          onSectionSaved={() => setStateRev((r) => r + 1)}
        />
      )}
    </div>
  );
}

// ── Site Info modal — read-only summary, opens via 👁 ───────────────────────

function SiteInfoModal({
  site,
  template,
  state,
  onClose,
}: {
  site: Site;
  template: Template;
  state: SiteState;
  onClose: () => void;
}) {
  // Coordinates string is built from lat/lng so it matches the SMRT
  // COP display format ("31.1664169, -105.6573331") even though we
  // store them as separate numeric fields under the hood.
  const coordsText =
    site.lat != null && site.lng != null
      ? `${site.lat}, ${site.lng}`
      : "";
  const meta = site.meta || {};

  return (
    <div className="smrtdocs-modal-overlay">
      <div
        className="smrtdocs-modal"
        style={{ width: "min(640px, 95vw)", maxHeight: "85dvh", display: "flex", flexDirection: "column" }}
      >
        <div
          className="smrtdocs-modal-header"
          style={{ flexDirection: "column", alignItems: "center", position: "relative" }}
        >
          <h2 style={{ margin: 0, fontSize: "1rem", textAlign: "center" }}>
            {site.name}
          </h2>
          <div style={{ fontSize: "0.75rem", color: "#9aa0a6", marginTop: 2 }}>
            {template.name}
          </div>
          <button
            className="btn-cancel"
            style={{ position: "absolute", right: 12, top: 10, padding: "4px 12px", fontSize: "0.8rem", height: 28 }}
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {/* Body uses the smrtcop-review-form-grid pattern: 2-column grid
            with span-2 cells for full-width fields. All inputs disabled
            so this stays a read-only summary. */}
        <div className="smrtdocs-modal-body" style={{ overflowY: "auto", flex: 1 }}>
          <div className="smrtcop-review-form-grid">
            <div className="smrtcop-field">
              <label className="smrtcop-field-label">Site ID</label>
              <input className="smrtcop-input" value={meta.siteCode || ""} disabled />
            </div>
            <div className="smrtcop-field">
              <label className="smrtcop-field-label">FA Location Code</label>
              <input className="smrtcop-input" value={meta.faLocationCode || ""} disabled />
            </div>

            <div className="smrtcop-field smrtcop-field-span">
              <label className="smrtcop-field-label">Physical Address</label>
              <input className="smrtcop-input" value={site.address || ""} disabled />
            </div>

            <div className="smrtcop-field smrtcop-field-span">
              <label className="smrtcop-field-label">Co-ordinates (Lat/Long)</label>
              <input className="smrtcop-input" value={coordsText} disabled />
            </div>

            <div className="smrtcop-field smrtcop-field-span">
              <label className="smrtcop-field-label">Azimuths</label>
              <div className="smrtcop-azimuth-row">
                {(["A", "B", "C"] as const).map((sector) => {
                  const v =
                    sector === "A" ? meta.azimuthA :
                    sector === "B" ? meta.azimuthB :
                    meta.azimuthC;
                  return (
                    <div key={sector} className="smrtcop-azimuth-cell">
                      <div className="smrtcop-azimuth-sublabel">Sector {sector}</div>
                      <input className="smrtcop-input" value={v || ""} disabled />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Section progress — same dark info card style as before, kept
                so the user can see what's done before they tap Start. */}
            <div className="smrtcop-field smrtcop-field-span" style={{ marginTop: 8 }}>
              <label className="smrtcop-field-label">Sections</label>
              <div className="sc-cop-info-card">
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {template.sections.length === 0 && (
                    <span style={{ color: "#9aa0a6", fontSize: "0.85rem" }}>None</span>
                  )}
                  {template.sections.map((sec) => {
                    const done = !!state.submitted[sec.id];
                    return (
                      <div key={sec.id} className="sc-cop-section-row">
                        <span
                          className="sc-cop-status-dot"
                          style={{ background: done ? "#1faa59" : "#3a3a3a" }}
                        />
                        <span style={{ flex: 1 }}>{sec.label}</span>
                        <span style={{ fontSize: "0.75rem", color: "#9aa0a6" }}>
                          {done ? "Submitted" : "Pending"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Section wizard — one section per step, all questions on one screen ─────

function SectionWizard({
  site,
  template,
  startIdx,
  userId,
  onClose,
  onSectionSaved,
}: {
  site: Site;
  template: Template;
  startIdx: number;
  userId: string;
  onClose: () => void;
  onSectionSaved: () => void;
}) {
  const [state, setState] = useState<SiteState>(() => ({
    siteId: site.id,
    answers: {},
    submitted: {},
    updatedAt: new Date().toISOString(),
  }));
  // Hydrate site state on mount (async — backend fetch). Until it
  // resolves the wizard renders against an empty state, which means
  // existing photos won't appear briefly. Acceptable tradeoff for a
  // single network round-trip on wizard open.
  useEffect(() => {
    let cancelled = false;
    loadSiteState(site.id)
      .then((s) => {
        if (!cancelled) setState(s);
      })
      .catch((err) =>
        console.error("[SiteCheck] failed to load site state:", err),
      );
    return () => {
      cancelled = true;
    };
  }, [site.id]);

  const [idx, setIdx] = useState(startIdx);

  // Photo row ⋮ menu — at most one row across the wizard is expanded at
  // a time. Mirrors SMRT COP's openPhotoMenuKey pattern; the photo's ID
  // is unique across the array (newId("photo")) so we don't need a
  // composite key.
  const [openPhotoMenuKey, setOpenPhotoMenuKey] = useState<string | null>(null);
  const togglePhotoMenu = (key: string) =>
    setOpenPhotoMenuKey((prev) => (prev === key ? null : key));

  // Location-panel expansion — separate from the ⋮ menu so the two can
  // coexist. One panel open at a time across the wizard; tapping a
  // different row's 📍 swaps; tapping the same row's 📍 again closes.
  const [expandedLocationPhotoId, setExpandedLocationPhotoId] = useState<
    string | null
  >(null);
  const toggleLocationPanel = (key: string) =>
    setExpandedLocationPhotoId((prev) => (prev === key ? null : key));

  // Rename modal — tracks both the question key (so we know which
  // PhotoEntry[] to update) and the photo id (so we know which entry).
  const [renamingPhoto, setRenamingPhoto] = useState<{
    questionKey: string;
    photoId: string;
    initialName: string;
  } | null>(null);
  const [renamingValue, setRenamingValue] = useState("");

  // Close the photo menu on outside click; bound only while one is open.
  useEffect(() => {
    if (!openPhotoMenuKey) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || !target.closest(".smrtcop-photo-row-actions")) {
        setOpenPhotoMenuKey(null);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [openPhotoMenuKey]);

  const total = template.sections.length;
  const section = template.sections[idx];
  const isLast = idx === total - 1;

  // Fire-and-forget persist. Saves are async but the UI doesn't wait
  // for the round-trip — failures log only so a flaky network doesn't
  // make the wizard feel laggy. If a save fails, the next save in the
  // sequence retries the full state.
  const persist = (next: SiteState) => {
    saveSiteState(next).catch((err) =>
      console.error("[SiteCheck] save state failed:", err),
    );
  };

  const setAnswer = (key: string, value: unknown) => {
    setState((prev) => {
      const next: SiteState = {
        ...prev,
        answers: { ...prev.answers, [key]: value },
        updatedAt: new Date().toISOString(),
      };
      persist(next);
      return next;
    });
  };

  // Update one PhotoEntry inside a photo question's array — used by the
  // rename modal and the voice-note flow to swap fields on the entry
  // without touching the underlying blob in storage.
  const updatePhotoEntry = (
    questionKey: string,
    photoId: string,
    patch: Partial<PhotoEntry>,
  ) => {
    setState((prev) => {
      const list = Array.isArray(prev.answers[questionKey])
        ? (prev.answers[questionKey] as PhotoEntry[])
        : [];
      const next: SiteState = {
        ...prev,
        answers: {
          ...prev.answers,
          [questionKey]: list.map((p) =>
            p.id === photoId ? { ...p, ...patch } : p,
          ),
        },
        updatedAt: new Date().toISOString(),
      };
      persist(next);
      return next;
    });
  };

  const openRenameFor = (
    questionKey: string,
    photoId: string,
    fileName: string,
  ) => {
    setRenamingPhoto({ questionKey, photoId, initialName: fileName });
    setRenamingValue(fileName.replace(/\.[^.]+$/, ""));
    setOpenPhotoMenuKey(null);
  };

  const commitRename = () => {
    if (!renamingPhoto) return;
    const safe = renamingValue.trim().replace(/[\\/:*?"<>|]/g, "_");
    if (!safe) return;
    const ext = renamingPhoto.initialName.match(/\.[^.]+$/)?.[0] || ".jpg";
    const finalName = safe.endsWith(ext) ? safe : `${safe}${ext}`;
    updatePhotoEntry(renamingPhoto.questionKey, renamingPhoto.photoId, {
      fileName: finalName,
    });
    setRenamingPhoto(null);
  };

  const isAnswered = (q: Question, val: unknown): boolean => {
    if (q.type === "text") return typeof val === "string" && val.trim().length > 0;
    if (q.type === "yesNo") return val === "yes" || val === "no";
    if (q.type === "photo") return Array.isArray(val) && val.length > 0;
    return false;
  };

  // Section is "ready" (Continue turns green) when every required
  // question in it is answered. Optional questions don't gate Continue.
  const sectionReady = !!section && section.questions.every(
    (q) => !q.required || isAnswered(q, state.answers[q.key]),
  );

  const submitAndAdvance = () => {
    if (!section || !sectionReady) return;
    // Mark this section submitted, fire the notification stub, then
    // advance (or close on last section).
    const nextState: SiteState = {
      ...state,
      submitted: { ...state.submitted, [section.id]: true },
      updatedAt: new Date().toISOString(),
    };
    persist(nextState);
    setState(nextState);
    notifySectionCompletedStub({
      siteId: site.id,
      siteName: site.name,
      sectionLabel: section.label,
      completerUserId: userId,
    });
    onSectionSaved();
    if (isLast) {
      onClose();
    } else {
      setIdx((i) => i + 1);
    }
  };

  if (!section) {
    return (
      <div className="smrtdocs-modal-overlay">
        <div className="smrtdocs-modal" style={{ padding: 20 }}>
          <p style={{ color: "#cfcfcf" }}>This template has no sections.</p>
          <button className="btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="smrtdocs-modal-overlay">
      <div
        className="smrtdocs-modal"
        style={{ width: "min(700px, 95vw)", maxHeight: "90dvh", display: "flex", flexDirection: "column" }}
      >
        <div
          className="smrtdocs-modal-header"
          style={{ flexDirection: "column", alignItems: "center", position: "relative" }}
        >
          <h2 style={{ margin: 0, fontSize: "1rem", textAlign: "center" }}>
            {site.name}
          </h2>
          <div style={{ fontSize: "0.75rem", color: "#9aa0a6", marginTop: 2 }}>
            Section {idx + 1} of {total}
          </div>
          <button
            className="btn-cancel"
            style={{ position: "absolute", right: 12, top: 10, padding: "4px 12px", fontSize: "0.8rem", height: 28 }}
            onClick={onClose}
          >
            Exit
          </button>
        </div>

        <div className="smrtdocs-modal-body" style={{ overflowY: "auto", flex: 1 }}>
          <h3 className="sc-cop-section-title">{section.label}</h3>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {section.questions.length === 0 && (
              <p style={{ color: "#9aa0a6", textAlign: "center" }}>
                This section has no questions.
              </p>
            )}
            {section.questions.map((q) => (
              <QuestionCard
                key={q.key}
                site={site}
                question={q}
                value={state.answers[q.key]}
                onChange={(v) => setAnswer(q.key, v)}
                openPhotoMenuKey={openPhotoMenuKey}
                togglePhotoMenu={togglePhotoMenu}
                expandedLocationPhotoId={expandedLocationPhotoId}
                toggleLocationPanel={toggleLocationPanel}
                onRequestRename={openRenameFor}
              />
            ))}
          </div>
        </div>

        {/* Footer — Back on left, Skip + Continue cluster on right.
            Mirrors SMRT COP's wizard footer (smrtcop-btn-action +
            smrtcop-btn-cancel/confirm) so the buttons look identical
            to that flow. */}
        <div
          style={{
            padding: "12px 20px",
            display: "flex",
            justifyContent: "space-between",
            borderTop: "1px solid #555",
          }}
        >
          <div>
            {idx > 0 && (
              <button
                className="smrtcop-btn-action smrtcop-btn-cancel"
                onClick={() => setIdx((i) => i - 1)}
              >
                Back
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {!isLast && (
              <button
                className="smrtcop-btn-action smrtcop-btn-cancel"
                onClick={() => setIdx((i) => i + 1)}
              >
                Skip
              </button>
            )}
            <button
              className={`smrtcop-btn-action ${sectionReady ? "smrtcop-btn-confirm" : "smrtcop-btn-cancel"}`}
              disabled={!sectionReady}
              onClick={submitAndAdvance}
            >
              {isLast ? "Submit" : "Continue"}
            </button>
          </div>
        </div>
      </div>
      {renamingPhoto !== null && (
        <div className="smrtdocs-modal-overlay" style={{ zIndex: 10001 }}>
          <div className="smrtdocs-modal" style={{ width: "min(420px, 95vw)" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Rename Photo</h2>
            </div>
            <div className="smrtdocs-modal-body">
              <p style={{ fontSize: "0.82rem", color: "#cfcfcf", margin: "0 0 10px" }}>
                File extension is preserved automatically.
              </p>
              <input
                className="smrtcop-input"
                autoFocus
                value={renamingValue}
                onChange={(e) => setRenamingValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  else if (e.key === "Escape") setRenamingPhoto(null);
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 16,
                }}
              >
                <button className="btn-cancel" onClick={() => setRenamingPhoto(null)}>
                  Cancel
                </button>
                <button className="btn-save" onClick={commitRename}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Question card — wraps each question in a dark card ────────────────────

function QuestionCard({
  site,
  question,
  value,
  onChange,
  openPhotoMenuKey,
  togglePhotoMenu,
  expandedLocationPhotoId,
  toggleLocationPanel,
  onRequestRename,
}: {
  site: Site;
  question: Question;
  value: unknown;
  onChange: (v: unknown) => void;
  openPhotoMenuKey: string | null;
  togglePhotoMenu: (photoId: string) => void;
  expandedLocationPhotoId: string | null;
  toggleLocationPanel: (photoId: string) => void;
  onRequestRename: (questionKey: string, photoId: string, fileName: string) => void;
}) {
  return (
    <div className="sc-cop-question-card">
      <div className="sc-cop-question-label">
        {question.label}
        {question.required && <span className="sc-cop-required">*</span>}
      </div>

      {question.type === "text" && (
        <textarea
          className="sc-cop-textarea"
          rows={3}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your note…"
        />
      )}

      {question.type === "yesNo" && (
        <div className="sc-cop-yesno">
          <button
            className={`sc-cop-yesno-btn ${value === "yes" ? "active" : ""}`}
            onClick={() => onChange("yes")}
          >Yes</button>
          <button
            className={`sc-cop-yesno-btn no ${value === "no" ? "active" : ""}`}
            onClick={() => onChange("no")}
          >No</button>
        </div>
      )}

      {question.type === "photo" && (
        <PhotoSlot
          site={site}
          question={question}
          value={value}
          onChange={onChange}
          openPhotoMenuKey={openPhotoMenuKey}
          togglePhotoMenu={togglePhotoMenu}
          expandedLocationPhotoId={expandedLocationPhotoId}
          toggleLocationPanel={toggleLocationPanel}
          onRequestRename={onRequestRename}
        />
      )}
    </div>
  );
}

// ── Photo slot — list of rows + cam/upload cluster to add a new photo.
// Mirrors the SMRT COP photo-row visual: per-row [✓] filename [⋮], with
// view/voice-note/rename/delete behind the overflow menu, and a centered
// 📷 / ⤴ pair below for adding a new entry. ─────────────────────────────

function PhotoSlot({
  site,
  question,
  value,
  onChange,
  openPhotoMenuKey,
  togglePhotoMenu,
  expandedLocationPhotoId,
  toggleLocationPanel,
  onRequestRename,
}: {
  site: Site;
  question: Question;
  value: unknown;
  onChange: (v: unknown) => void;
  openPhotoMenuKey: string | null;
  togglePhotoMenu: (photoId: string) => void;
  expandedLocationPhotoId: string | null;
  toggleLocationPanel: (photoId: string) => void;
  onRequestRename: (questionKey: string, photoId: string, fileName: string) => void;
}) {
  const photos: PhotoEntry[] = Array.isArray(value) ? (value as PhotoEntry[]) : [];
  const camInputRef = useRef<HTMLInputElement>(null);
  const upInputRef = useRef<HTMLInputElement>(null);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("[SiteCheck] onPick fired", {
      filesLength: e.target.files?.length ?? 0,
      hasFile: !!e.target.files?.[0],
    });
    const file = e.target.files?.[0];
    if (!file) {
      console.warn("[SiteCheck] onPick: no file in input — early return");
      e.target.value = "";
      return;
    }
    console.log("[SiteCheck] onPick: file received", {
      name: file.name,
      type: file.type,
      size: file.size,
    });
    try {
      // Filename pattern: template question label + site name + .jpg.
      // Preserved verbatim through to the backend so admin downloads see
      // the same name. Multiple photos for the same question collide on
      // the default name; crew rename to disambiguate via ⋮ → ✎.
      const siteName = site.name || "site";
      const fileName = `${question.label}_${siteName}.jpg`;
      console.log("[SiteCheck] onPick: starting geo + upload", {
        siteId: site.id,
        fieldKey: question.key,
        fileName,
      });
      // Geo + upload happen in parallel — neither blocks the other.
      const [geo, uploaded] = await Promise.all([
        captureGeo(),
        uploadFile(site.id, question.key, file, fileName),
      ]);
      console.log("[SiteCheck] onPick: upload + geo done", {
        blobName: uploaded.blobName,
        hasGeo: !!geo,
      });
      const entry: PhotoEntry = {
        id: newId("photo"),
        fileName,
        blobName: uploaded.blobName,
        capturedAt: new Date().toISOString(),
        ...(geo
          ? {
              lat: geo.lat,
              lng: geo.lng,
              ...(geo.accuracy != null ? { accuracy: geo.accuracy } : {}),
            }
          : {}),
      };
      console.log("[SiteCheck] onPick: calling onChange with new entry", {
        entryId: entry.id,
        totalPhotos: photos.length + 1,
      });
      onChange([...photos, entry]);
    } catch (err) {
      console.error("[SiteCheck] photo upload failed:", err);
      alert("Photo upload failed. Check connection and try again.");
    } finally {
      e.target.value = "";
    }
  };

  const onDelete = async (photoId: string) => {
    const target = photos.find((p) => p.id === photoId);
    if (!target) return;
    // Best-effort blob cleanup. If the network drops we still drop the
    // entry from the answers so the row disappears from the UI; the
    // orphaned blob can be cleaned up later by an admin sweep.
    try {
      if (target.blobName) {
        await deleteFile(site.id, question.key, target.blobName);
      }
      if (target.voiceBlobName) {
        await deleteFile(
          site.id,
          `${question.key}__voice__${target.id}`,
          target.voiceBlobName,
        );
      }
    } catch (err) {
      console.warn("[SiteCheck] blob delete failed (entry still removed):", err);
    }
    onChange(photos.filter((p) => p.id !== photoId));
  };

  // Per-row partial update — used by the voice-note flow to attach /
  // detach the voice blob name without disturbing other PhotoEntry fields.
  const onUpdate = (photoId: string, patch: Partial<PhotoEntry>) => {
    onChange(
      photos.map((p) => (p.id === photoId ? { ...p, ...patch } : p)),
    );
  };

  // Hidden file inputs — single pair shared by every row's 📷/⤴ button
  // since they all do the same thing (append a new entry to the array).
  const triggerCam = () => camInputRef.current?.click();
  const triggerUp = () => upInputRef.current?.click();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <input
        ref={camInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={onPick}
      />
      <input
        ref={upInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onPick}
      />

      {photos.map((p) => (
        <PhotoRow
          key={p.id}
          site={site}
          questionKey={question.key}
          photo={p}
          isMenuOpen={openPhotoMenuKey === p.id}
          toggleMenu={() => togglePhotoMenu(p.id)}
          isLocationPanelOpen={expandedLocationPhotoId === p.id}
          toggleLocationPanel={() => toggleLocationPanel(p.id)}
          triggerCam={triggerCam}
          triggerUp={triggerUp}
          onRequestRename={() =>
            onRequestRename(question.key, p.id, p.fileName)
          }
          onDelete={() => onDelete(p.id)}
          onPhotoChange={(patch) => onUpdate(p.id, patch)}
        />
      ))}

      {/* When there are no photos yet, render a single empty starter row
          so the user has somewhere to tap 📷 / ⤴ from. Once any photo is
          added it disappears — every filled row carries the same actions. */}
      {photos.length === 0 && (
        <PhotoRow
          site={site}
          questionKey={question.key}
          photo={null}
          isMenuOpen={false}
          toggleMenu={() => undefined}
          isLocationPanelOpen={false}
          toggleLocationPanel={() => undefined}
          triggerCam={triggerCam}
          triggerUp={triggerUp}
          onRequestRename={() => undefined}
          onDelete={() => undefined}
          onPhotoChange={() => undefined}
        />
      )}
    </div>
  );
}

function PhotoRow({
  site,
  questionKey,
  photo,
  isMenuOpen,
  toggleMenu,
  isLocationPanelOpen,
  toggleLocationPanel,
  triggerCam,
  triggerUp,
  onRequestRename,
  onDelete,
  onPhotoChange,
}: {
  site: Site;
  questionKey: string;
  photo: PhotoEntry | null;
  isMenuOpen: boolean;
  toggleMenu: () => void;
  isLocationPanelOpen: boolean;
  toggleLocationPanel: () => void;
  triggerCam: () => void;
  triggerUp: () => void;
  onRequestRename: () => void;
  onDelete: () => void;
  onPhotoChange: (patch: Partial<PhotoEntry>) => void;
}) {
  const isFilled = photo !== null;
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [photoLightboxUrl, setPhotoLightboxUrl] = useState<string | null>(null);

  // Resolve the photo's blob to a revocable object URL once per row mount
  // so View renders instantly in the lightbox. Bytes stream through the
  // backend proxy (?format=base64) — never the storage account directly,
  // which is firewalled to backend IPs. Empty starter rows skip the
  // lookup. The object URL is revoked on unmount / blob change.
  useEffect(() => {
    if (!photo || !photo.blobName) return;
    let url: string | null = null;
    let cancelled = false;
    getFileObjectUrl(site.id, questionKey, photo.blobName)
      .then((u) => {
        url = u;
        if (cancelled) URL.revokeObjectURL(u);
        else setPreviewUrl(u);
      })
      .catch((err) =>
        console.error("[SiteCheck] failed to fetch photo URL:", err),
      );
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
      setPreviewUrl(null);
    };
  }, [site.id, questionKey, photo]);

  // iOS WKWebView (Capacitor) silently ignores window.open() of a blob:
  // URL, so View renders the proxied photo in an in-app lightbox instead.
  // The lightbox reuses previewUrl — revocation stays owned by the effect
  // above; closing the lightbox only clears the state, never revokes.
  const handleView = () => {
    if (previewUrl) setPhotoLightboxUrl(previewUrl);
  };

  // Geo info goes in the row's title attribute so the user can see it on
  // hover without it taking up row real-estate.
  const titleParts: string[] = [];
  if (photo) {
    titleParts.push(photo.fileName);
    if (photo.lat != null && photo.lng != null) {
      titleParts.push(`📍 ${photo.lat.toFixed(5)}, ${photo.lng.toFixed(5)}`);
    } else {
      titleParts.push("📍 no location");
    }
  }

  // hasGeo gates both the passive "Location recorded" indicator and the
  // 📍 toolbar button; with no lat/lng there's nothing to show. Rendered
  // outside the row chrome so the row itself stays single-line.
  const hasGeo = !!(photo && photo.lat != null && photo.lng != null);
  const capturedDisplay = photo?.capturedAt
    ? new Date(photo.capturedAt).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

  return (
    <div className="sc-loc-row-wrap">
      <div className={`smrtcop-photo-row${isFilled ? " filled" : ""}`}>
        <span
          className={`smrtcop-photo-row-status${isFilled ? " done" : ""}`}
          aria-hidden
        >
          {isFilled ? "✓" : ""}
        </span>
        <span
          className="smrtcop-photo-row-name"
          title={titleParts.join("\n")}
          style={!isFilled ? { color: "#5a5a5a" } : undefined}
        >
          {photo?.fileName || ""}
        </span>
        {/* Action cluster — every row carries 📷, ⤴, 📍, ⋮ by default;
            the ⋮ slot reveals view / voice / edit / delete inline before
            ⋮ when expanded so the row never wraps. Empty starter rows
            hide both 📍 and ⋮ (nothing to act on). */}
        <span className="smrtcop-photo-row-actions">
          <button
            type="button"
            className="smrtcop-photo-row-btn smrtcop-photo-row-cam"
            onClick={triggerCam}
            title="Take photo"
          >
            📷
          </button>
          <button
            type="button"
            className="smrtcop-photo-row-btn smrtcop-photo-row-up"
            onClick={triggerUp}
            title="Upload from photos"
          >
            ⤴
          </button>
          {isFilled && isMenuOpen && (
            <>
              <button
                type="button"
                className="smrtcop-photo-row-btn smrtcop-photo-row-view"
                onClick={handleView}
                disabled={!previewUrl}
                title="View"
                role="menuitem"
              >
                👁
              </button>
              <VoiceNoteControl
                site={site}
                questionKey={questionKey}
                photo={photo!}
                onVoiceBlobChange={(blobName) =>
                  onPhotoChange({ voiceBlobName: blobName })
                }
              />
              <button
                type="button"
                className="smrtcop-photo-row-btn smrtcop-photo-row-edit"
                onClick={onRequestRename}
                title="Rename"
                role="menuitem"
              >
                ✎
              </button>
              <button
                type="button"
                className="smrtcop-photo-row-btn smrtcop-photo-row-del"
                onClick={onDelete}
                title="Delete"
                role="menuitem"
              >
                🗑
              </button>
            </>
          )}
          {isFilled && (
            <button
              type="button"
              className="sc-loc-btn"
              onClick={toggleLocationPanel}
              disabled={!hasGeo}
              aria-expanded={isLocationPanelOpen}
              title={
                hasGeo
                  ? isLocationPanelOpen
                    ? "Hide location details"
                    : "Show location details"
                  : "No location captured"
              }
            >
              📍
            </button>
          )}
          {isFilled && (
            <button
              type="button"
              className="smrtcop-photo-row-btn smrtcop-photo-row-more"
              onClick={(e) => {
                e.stopPropagation();
                toggleMenu();
              }}
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              title={isMenuOpen ? "Hide actions" : "More actions"}
            >
              ⋮
            </button>
          )}
        </span>
      </div>
      {/* Passive indicator — always shown when the photo has geo data;
          subtle styling so it sits below the row without competing with
          the filename. Tap-target stays on the 📍 toolbar button above. */}
      {hasGeo && (
        <div className="sc-loc-indicator">📍 Location recorded</div>
      )}
      {/* Expandable details panel — toggled by the 📍 toolbar button.
          Read-only; one open at a time across the wizard. */}
      {hasGeo && isLocationPanelOpen && photo && (
        <div className="sc-loc-panel" role="region" aria-label="Location details">
          <div className="sc-loc-panel-title">📍 Location Details</div>
          <div className="sc-loc-panel-row">
            <span className="sc-loc-panel-label">Latitude</span>
            <span className="sc-loc-panel-value">
              {photo.lat!.toFixed(5)}
            </span>
          </div>
          <div className="sc-loc-panel-row">
            <span className="sc-loc-panel-label">Longitude</span>
            <span className="sc-loc-panel-value">
              {photo.lng!.toFixed(5)}
            </span>
          </div>
          <div className="sc-loc-panel-row">
            <span className="sc-loc-panel-label">Accuracy</span>
            <span className="sc-loc-panel-value">
              {photo.accuracy != null
                ? `${Math.round(photo.accuracy)} m`
                : "—"}
            </span>
          </div>
          <div className="sc-loc-panel-row">
            <span className="sc-loc-panel-label">Captured</span>
            <span className="sc-loc-panel-value">{capturedDisplay}</span>
          </div>
        </div>
      )}
      {/* In-app photo lightbox — see handleView. Reuses the SMRT Docs
          modal overlay chrome. The image src is the proxied object URL
          held in previewUrl; do not revoke it here (the prefetch effect
          owns its lifecycle). */}
      {photoLightboxUrl && (
        <div
          className="smrtdocs-modal-overlay"
          style={{ zIndex: 10002 }}
          onClick={() => setPhotoLightboxUrl(null)}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "95vw",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={photoLightboxUrl}
              alt={photo?.fileName || "Photo"}
              style={{
                maxWidth: "95vw",
                maxHeight: "85vh",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
            <button
              type="button"
              className="btn-cancel"
              style={{ marginTop: 12 }}
              onClick={() => setPhotoLightboxUrl(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Voice note — record / play / re-record / delete. Shares the
// smrtcop-photo-row-btn shell so it lines up with the other action
// buttons in the overflow menu; specific colour variants flag recording
// and playback states. ────────────────────────────────────────────────────

// MediaRecorder MIME selection — iOS WKWebView prefers audio/mp4 (AAC),
// Chrome/Android prefer audio/webm. Probe in order; fall back to the
// browser default (empty string) which lets MediaRecorder pick whatever
// it can support. Computed once per record-start to be cheap and safe.
function pickRecorderMime(): string {
  if (typeof MediaRecorder === "undefined") return "";
  if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4";
  if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
  return "";
}

function VoiceNoteControl({
  site,
  questionKey,
  photo,
  onVoiceBlobChange,
}: {
  site: Site;
  questionKey: string;
  photo: PhotoEntry;
  onVoiceBlobChange: (blobName: string | undefined) => void;
}) {
  // hasNote derives synchronously from the photo's stored blob name —
  // no separate lookup needed. The PhotoEntry is the single source of
  // truth; recording sets voiceBlobName, deletion clears it.
  const hasNote = !!photo.voiceBlobName;
  // Per-photo voice-note field key. Distinct from the photo's own field
  // key so the two blobs don't share a prefix (and can't collide with
  // each other or with another photo's voice note).
  const voiceFieldKey = `${questionKey}__voice__${photo.id}`;

  const [recording, setRecording] = useState(false);
  const [playing, setPlaying] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickRecorderMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        // Use the recorder's actual mimeType so the stored Blob's type
        // matches what was recorded — playback can then pick the right
        // codec. Fall back to webm if the browser doesn't expose
        // mimeType (very old engines).
        const blob = new Blob(chunksRef.current, {
          type: rec.mimeType || mime || "audio/webm",
        });
        const ext =
          (rec.mimeType || mime).includes("mp4") ? "m4a" :
          (rec.mimeType || mime).includes("webm") ? "webm" :
          "audio";
        try {
          const uploaded = await uploadFile(
            site.id,
            voiceFieldKey,
            blob,
            `voice_${photo.id}.${ext}`,
          );
          onVoiceBlobChange(uploaded.blobName);
        } catch (err) {
          console.error("[SiteCheck] voice-note upload failed", err);
          alert("Could not save voice note. Check connection and try again.");
        }
        stream.getTracks().forEach((t) => t.stop());
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      console.error("[SiteCheck] mic error", err);
      alert("Microphone unavailable. Check browser permissions.");
    }
  };

  const stopRecord = () => {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  };

  const playNote = async () => {
    if (!photo.voiceBlobName) return;
    try {
      // getFileUrl streams the blob through the backend proxy and returns
      // a data: URL — new Audio() accepts it, and it avoids both the
      // firewalled storage account and the SAS-URL CORS quirks WKWebView
      // sometimes hits when an <audio> source crosses origins.
      const dataUrl = await getFileUrl(
        site.id,
        voiceFieldKey,
        photo.voiceBlobName,
      );
      const a = new Audio(dataUrl);
      audioRef.current = a;
      a.onended = () => setPlaying(false);
      a.play();
      setPlaying(true);
    } catch (err) {
      console.error("[SiteCheck] voice-note playback failed", err);
    }
  };

  const stopPlay = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(false);
  };

  const removeNote = async () => {
    if (!photo.voiceBlobName) return;
    try {
      await deleteFile(site.id, voiceFieldKey, photo.voiceBlobName);
    } catch (err) {
      console.warn("[SiteCheck] voice-note delete failed (clearing entry):", err);
    }
    onVoiceBlobChange(undefined);
  };

  if (recording) {
    return (
      <button
        type="button"
        className="smrtcop-photo-row-btn smrtcop-photo-row-mic-rec"
        onClick={stopRecord}
        title="Stop recording"
        role="menuitem"
      >
        ⏹
      </button>
    );
  }
  if (playing) {
    return (
      <button
        type="button"
        className="smrtcop-photo-row-btn smrtcop-photo-row-mic-play"
        onClick={stopPlay}
        title="Stop playback"
        role="menuitem"
      >
        ⏸
      </button>
    );
  }
  if (hasNote) {
    // Has a note — single button cycles play; long-press / right-click
    // not used. Re-record happens by deleting first then recording again.
    // Delete flow: click the mic when a note exists doesn't directly
    // delete; user opens the row's menu and clicks the bin. Voice note
    // delete is handled implicitly by photo delete in onDelete above.
    return (
      <button
        type="button"
        className="smrtcop-photo-row-btn smrtcop-photo-row-mic-has"
        onClick={playNote}
        onContextMenu={(e) => {
          e.preventDefault();
          if (window.confirm("Remove voice note?")) removeNote();
        }}
        title="Play voice note (long-press / right-click to delete)"
        role="menuitem"
      >
        🔊
      </button>
    );
  }
  return (
    <button
      type="button"
      className="smrtcop-photo-row-btn smrtcop-photo-row-mic"
      onClick={startRecord}
      title="Record voice note"
      role="menuitem"
    >
      🎤
    </button>
  );
}

